import React, { useEffect } from "react";

import { motion } from "framer-motion";
import { Lock, ShieldCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { Loader } from "../common/loader";
import mailImg from "../../assets/signin/Mail.svg";
import {
  CUSTOMER_HOME_PATH,
  ROLES,
  TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS,
} from "../helpers/constants";
import { userSigupAction } from "../../redux/slices/user/userSignUpSlice";
import { userSigInAction } from "../../redux/slices/user/userSignInSlice";
import { Button as TailwindButton } from "../common/customDesignedComponents/button";
import { propertyRiskManagementAction } from "../../redux/slices/admin/property-risk-management/propertyRiskManagementSlice";
import {
  clearSignupResumeMarkers,
  encryptPayload,
  getObjLength,
  resolveStoredProductTier,
  setStanupPopupCountIfEligible,
} from "../helpers/utils";

const FinalStepCompleted = ({
  isAdminCreateUser = false,
  isServiceProvider = false,
  completedProcess = () => {},
  isTailwindComponent = false,
}) => {
  const { userEmail, sendMailLoader, userSignInDetails, userLoginLoader } =
    useSelector((state) => state.signUpUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const adminCreateSuccessPath = isServiceProvider ? "/clients" : "/admin-users";
  const adminCreateSuccessLabel = isServiceProvider
    ? "Go To Dashboard"
    : "Go to User's List";
  const userDataFromSession = JSON.parse(
    localStorage.getItem("userSignupDetails"),
  );

  const clearSignupEphemera = () => {
    sessionStorage.removeItem("userSignupDetails");
    localStorage.removeItem("userSignupDetails");
    localStorage.removeItem("errorCount");
    localStorage.removeItem("skip");
    localStorage.removeItem("promoCode");
    clearSignupResumeMarkers();
    dispatch(userSigupAction.clearData());
    dispatch(userSigupAction.clearResumeProcessData());
    dispatch(userSigInAction?.clearStoreData());
  };

  /**
   * Enter the account with the session already established (web auto-login or
   * native login-after-OTP). Never wipe localStorage here — that used to clear
   * authToken and force a second login inside the app webview.
   */
  const enterAccount = () => {
    clearSignupEphemera();

    if (localStorage.getItem("authToken")) {
      navigate(CUSTOMER_HOME_PATH, { replace: true });
      return;
    }

    navigate("/login", { replace: true });
  };

  useEffect(() => {
    if (userSignInDetails?.success && !isAdminCreateUser) {
      clearSignupEphemera();

      if (getObjLength(userSignInDetails) && userSignInDetails?.success) {
        let uID = userSignInDetails?.data?.user?.id;
        localStorage.setItem("uID", uID);
        dispatch(
          propertyRiskManagementAction.resetPropertyRiskManagementState(),
        );
        localStorage.setItem("roles", encryptPayload(ROLES.customer));
        setStanupPopupCountIfEligible(
          userSignInDetails?.data?.category ?? resolveStoredProductTier(),
        );
        localStorage.setItem("paymentPopup", 0);

        if (localStorage.getItem("authToken")) {
          // Web, iOS, and Android all land on the customer dashboard.
          navigate(CUSTOMER_HOME_PATH, { replace: true });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSignInDetails]);

  if (isTailwindComponent) {
    const emailDisplay = userEmail
      ? userEmail
      : getObjLength(userDataFromSession)
        ? userDataFromSession?.email
        : "your registered email";

    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
              <Lock className="h-4 w-4 text-white" />
              <span className="text-white font-medium text-sm">
                Secure connection
              </span>
            </div>

            <div className="p-8 md:p-10 text-center">
              <div className="flex justify-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-20 h-20 rounded-full bg-gradient-to-br from-secondary/20 to-cyan-400/20 flex items-center justify-center"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-secondary to-cyan-400 flex items-center justify-center">
                    <ShieldCheck className="w-8 h-8 text-white" />
                  </div>
                </motion.div>
              </div>

              <h1 className="text-2xl font-semibold text-foreground mb-3">
                Success
              </h1>

              {isAdminCreateUser ? (
                <p className="text-muted-foreground mb-6">
                  The user account has been successfully created!
                </p>
              ) : (
                <p className="text-muted-foreground mb-6">
                  This is a success message. We sent an email to{" "}
                  <span className="font-semibold text-foreground">
                    {emailDisplay}
                  </span>{" "}
                  with the confirmation and the details of your account.
                </p>
              )}

              <div className="flex w-full justify-center mb-8">
                <img
                  src={mailImg}
                  alt="Mail"
                  className="mx-auto block max-h-24 w-auto"
                />
              </div>

              <div className="flex justify-center">
                {isAdminCreateUser ? (
                  <Link to={adminCreateSuccessPath} className="inline-block">
                    <TailwindButton
                      onClick={completedProcess}
                      className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-medium px-10 py-3 text-base rounded-full shadow-md hover:shadow-lg transition-all"
                    >
                      {adminCreateSuccessLabel}
                    </TailwindButton>
                  </Link>
                ) : (
                  <TailwindButton
                    onClick={enterAccount}
                    className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-medium px-10 py-3 text-base rounded-full shadow-md hover:shadow-lg transition-all"
                  >
                    Go to Dashboard
                  </TailwindButton>
                )}
              </div>
            </div>
          </div>
        </motion.div>
        <Loader open={sendMailLoader || userLoginLoader} />
      </>
    );
  }

  return (
    <Box className="accountBoxWrapper">
      <Typography align="center" variant="h3">
        Success
      </Typography>
      {isAdminCreateUser ? (
        <Typography align="center" variant="body1">
          The user account has been successfully created!
        </Typography>
      ) : (
        <Typography align="center" variant="body1">
          This is a success message. We sent an email to&nbsp;
          {userEmail ? (
            <b>{userEmail}</b>
          ) : getObjLength(userDataFromSession) ? (
            <b>{userDataFromSession?.email}</b>
          ) : (
            `your registered email`
          )}
          &nbsp; with the confirmation and the details of your account.
        </Typography>
      )}
      <Box
        className="text-center"
        py={2}
        sx={{ display: "flex", justifyContent: "center", width: "100%" }}
      >
        <img
          src={mailImg}
          alt="sign"
          style={{ display: "block", margin: "0 auto", maxWidth: "100%" }}
        />
      </Box>
      <div className="text-center stepBtn mt-5 tailwind_component">
        {isAdminCreateUser ? (
          <Link
            className="text-white text-decoration-none"
            to={adminCreateSuccessPath}
          >
            <Button
              onClick={completedProcess}
              className={` ${TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS} mt-3 rounded-full ring-2 ring-secondary/30 ring-offset-2`}
              variant="contained"
            >
              {adminCreateSuccessLabel}
            </Button>
          </Link>
        ) : (
          <Button
            onClick={enterAccount}
            className="next_button mt-3"
            variant="contained"
          >
            Go to Dashboard
          </Button>
        )}
      </div>
      <Loader open={sendMailLoader || userLoginLoader} />
    </Box>
  );
};

export default FinalStepCompleted;
